import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { EditAutomationFunctionsComponent } from 'src/app/components/edit-automation-functions/edit-automation-functions.component';
import { SlackMessageComponent } from 'src/app/components/slack-message/slack-message.component';
import { SlackTextEditorComponent } from 'src/app/components/slack-text-editor/slack-text-editor.component';
import {
  AUTOMATION_ACTION_TYPES_ICONS,
  AUTOMATION_ACTION_TYPES_LABELS,
  STATUS_COLUMN_FORMAT_MAP,
  SUPPORT_STEP_ASSIGNEE_TYPES,
  DEFAULT_PRIORITY_TOKENS,
  SUPPORT_STEP_KEYWORDS,
  CONVERSATION_STATUS,
} from 'src/app/constants';
import { extractTicketId, getVisibleFields } from 'src/app/utils/custom-fields';
import { isHtml, getOrderedPriorityOptions, isUrl, createPriorityValueToTokenMap } from 'src/app/utils/utils';
import {
  AUTOMATION_ACTION_TYPES,
  CUSTOM_FIELD_TYPES,
  ListResponse,
  CustomField,
  PriorityTokens,
  SlackChannel,
} from 'src/models';
import {
  CONVERSATION_LINK_TYPES,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS,
} from 'src/models/conversation-links';
import { ENTITY_TYPES } from 'src/models/entities';
import { ColonToEmojiPipe } from 'src/pipes/colonToEmoji.pipe';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { AutomationsService } from 'src/services/automations.service';
import { CustomFieldsService } from 'src/services/custom-fields.service';
import { EmojisService } from 'src/services/emojis.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService, STATUS_OPTIONS } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';
import { TicketTypesService } from 'src/services/ticket-types.service';
import { TokenService } from 'src/services/token.service';
import { UsersService } from 'src/services/users.service';

import { EmojiPickerComponent } from '../../components/emoji-picker/emoji-picker.component';
import { TicketTypeFieldsComponent } from '../../components/ticket-type-fields/ticket-type-fields.component';
import { AutomationCustomFieldInputComponent } from '../automation-custom-field-input/automation-custom-field-input.component';

const STANDARD_RESOLVABLE_ENTITY_FIELDS: any[] = [
  {
    id: 'conversation.submitterUserId',
    title: 'Submitter',
    object: {
      type: ENTITY_TYPES.USER,
    },
    icon: 'flash',
  },
  {
    id: 'conversation.assignedToUserId',
    title: 'Assignee',
    object: {
      type: ENTITY_TYPES.USER,
    },
    icon: 'flash',
  },
  {
    id: 'user.id',
    title: 'Triggering User',
    object: {
      type: ENTITY_TYPES.USER,
    },
    icon: 'flash',
  },
  {
    id: SUPPORT_STEP_KEYWORDS.PRIMARY_SUPPORT_ASSIGNEE,
    title: 'Primary Support Rep',
    object: {
      isValue: true,
    },
    icon: 'flash',
  },
  {
    id: SUPPORT_STEP_KEYWORDS.SECONDARY_SUPPORT_ASSIGNEE,
    title: 'Secondary Support Rep',
    object: {
      isValue: true,
    },
    icon: 'flash',
  },
];

const STANDARD_RESOLVABLE_STRING_FIELDS: any[] = [
  {
    id: 'conversation.title',
    title: 'Conversation Title',
    description: 'Title of the triggering conversation',
  },
  {
    id: 'conversation.summary',
    title: 'Conversation Summary',
    description: 'Summary of the triggering conversation',
  },
  {
    id: 'conversation.status',
    title: 'Conversation Status',
    description: 'Status of the triggering conversation',
  },
  {
    id: 'conversation.priority',
    title: 'Triggering Conversation Priority',
    description: 'Priority of the triggering conversation',
  },
  {
    id: 'conversation.projectId',
    title: 'Triggering Conversation Project',
    description: 'Project of the triggering conversation',
  },
  {
    id: 'conversation.ticketTypeId',
    title: 'Conversation Ticket Type',
    description: 'Ticket type of the triggering conversation',
  },
  {
    id: 'conversation.customerId',
    title: 'Conversation Customer',
    description: 'Customer of the triggering conversation',
  },
  {
    id: 'conversation.friendlyId',
    title: 'Conversation ID',
    description: 'Human-readable ID of the conversation',
  },
  {
    id: 'conversation.customer.name',
    title: 'Customer Name',
    description: 'Name of the customer',
  },
  {
    id: 'conversation.submitterUser.name',
    title: 'Submitter Name',
    description: 'Name of the person who submitted the conversation',
  },
  {
    id: 'conversation.assignedToUser.name',
    title: 'Assignee Name',
    description: 'Name of the assigned user',
  },
  {
    id: 'user.name',
    title: 'Triggering User Name',
    description: 'Name of the user who triggered the automation',
  },
];

// Event labels for formatting
const AUTOMATION_TRIGGER_OBJECT_TYPES = {
  CONVERSATION: 'conversation',
} as const;

const AUTOMATION_TRIGGER_EVENTS = {
  [AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION]: {
    CREATED: 'created',
    UPDATED: 'updated',
    APPROVAL_REQUEST_APPROVED: 'approvalRequestApproved',
    APPROVAL_REQUEST_REJECTED: 'approvalRequestRejected',
    APPROVAL_REQUEST_CREATED: 'approvalRequestCreated',
    MESSAGE_ADDED: 'messageAdded',
    INTERNAL_MESSAGE_ADDED: 'internalMessageAdded',
    EXTERNAL_MESSAGE_ADDED: 'externalMessageAdded',
    CLOSED: 'closed',
    REOPENED: 'reopened',
    PUT_ON_HOLD: 'putOnHold',
    INTERNAL_UNRESPONSIVE: 'internalUnresponsive',
    EXTERNAL_UNRESPONSIVE: 'externalUnresponsive',
    TRIAGED: 'triaged',
    TRIAGE_MESSAGE_ADDED: 'triageMessageAdded',
    ESCALATION_CREATED: 'escalationCreated',
    ESCALATION_ACKNOWLEDGED: 'escalationAcknowledged',
    ESCALATION_RESOLVED: 'escalationResolved',
  },
} as const;

@Component({
  selector: 'app-automation-action-step',
  templateUrl: './automation-action-step.component.html',
  styleUrls: ['./automation-action-step.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    SlackTextEditorComponent,
    SlackMessageComponent,
    NgxTippyModule,
    AutomationCustomFieldInputComponent,
    ChannelSelectorModule,
    EmojiPickerComponent,
    RouterModule,
    TicketTypeFieldsComponent,
    ColonToEmojiPipe,
  ],
})
export class AutomationActionStepComponent implements OnInit, OnChanges {
  @Input() index: number;
  @Input() step: any;
  @Input() lookups: Record<string, Record<string, string>> = {};
  @Input() functionNames: string[] = [];
  @Input() functionsVersions: any[] = [];
  @Input() canDrag = false;
  @Input() inputFields: any[] = [];
  @Input() projectId: string | null = null;
  @Input() triggerType: string | null = null;
  @Output() remove = new EventEmitter<void>();
  @Output() actionDidChange = new EventEmitter<void>();

  priorityLabels: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS;
  priorityOptionsForTemplate: { value: number; label: string }[] = [];
  priorityValueToTokenMap: Record<number, PriorityTokens> = createPriorityValueToTokenMap(DEFAULT_PRIORITY_TOKENS);

  // Ticket type and custom fields
  projectFieldDefinitions: CustomField[] = [];
  ticketTypeFieldDefinitions: CustomField[] = [];
  renderableFieldDefinitionsCache: Record<string, CustomField[]> = {};

  // New conversation link state (filter-style)

  public AUTOMATION_ACTION_TYPES = AUTOMATION_ACTION_TYPES;
  public AUTOMATION_ACTION_TYPES_ICONS = AUTOMATION_ACTION_TYPES_ICONS;
  public AUTOMATION_ACTION_TYPES_LABELS = AUTOMATION_ACTION_TYPES_LABELS;

  public CONVERSATION_STATUS_LABELS: Record<string, string> = {
    [CONVERSATION_STATUS.OPEN]: 'Needs Response',
    [CONVERSATION_STATUS.IN_PROGRESS]: 'In Progress',
    [CONVERSATION_STATUS.ON_HOLD]: 'On Hold',
    [CONVERSATION_STATUS.CLOSED]: 'Closed',
    'conversation.status': 'Triggering Conversation Status',
  };

  editorOptions = {
    theme: 'vs-dark',
    language: 'typescript',
    automaticLayout: true,
  };
  code: string = '';
  isLoadingLookups = false;

  ENTITY_TYPES = ENTITY_TYPES;

  // Stored hydrated value for the custom field input
  hydratedCustomFieldValue: any = null;

  // Stored hydrated value for the emoji input
  hydratedEmojiValue: any = null;

  selectedCustomFieldForAction: CustomField | null = null;

  public isHTML = isHtml;
  public isURL = isUrl;

  // Toggle for advanced settings in wait action
  showAdvancedSettings = false;

  constructor(
    private searchablePopoverService: SearchablePopoverService,
    private modalCtrl: ModalController,
    private automationsService: AutomationsService,
    private msgSrvc: MessageService,
    private popoverCtrl: PopoverController,
    private apiService: ApiService,
    private customFieldsService: CustomFieldsService,
    private tokenService: TokenService,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    public authService: AuthService,
    private emojiService: EmojisService,
    private projectsService: ProjectsService,
    private usersService: UsersService,
    private slackService: SlackService,
    private ticketTypesService: TicketTypesService,
  ) {}

  async ngOnInit() {
    // Lazy load template html as needed
    await this.loadPriorityLabels();

    // Load field definitions for CREATE_CONVERSATION actions
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION) {
      await this.loadFieldDefinitions();
    }

    if (this.step?.data?.action?.data?.templateId) {
      this.apiService.get(`/templates/${this.step.data.action.data.templateId}`).subscribe(async (template: any) => {
        setTimeout(() => {
          if (!this.lookups.templateHtml) {
            this.lookups.templateHtml = {};
          }
          this.lookups.templateHtml[this.step.data.action.data.templateId] = template.html;
        }, 100);
      });
    }
    if (this.step?.data?.action?.data?.emoji) {
      this.hydratedEmojiValue = await this.emojiService.resolveEmojiToDisplay(this.step.data.action.data.emoji);
    }

    // Initialize state if the current step is SET_CUSTOM_FIELD
    await this.initializeSetCustomFieldState();

    // Initialize isAutoresponse default value for SEND_SLACK_TEMPLATE actions
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SEND_SLACK_TEMPLATE) {
      if (this.step.data.action.data.isAutoresponse === undefined) {
        this.step.data.action.data.isAutoresponse = true;
      }
    }

    // Initialize sendAs default value for CREATE_CONVERSATION actions
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION) {
      if (this.step.data.action.data.sendAs === undefined) {
        this.step.data.action.data.sendAs = 'support_team';
      }

      // Load user information for entityId if it exists
      if (this.step.data.action.data.entityId) {
        this.loadUserForEntityId(this.step.data.action.data.entityId);
      }
    }

    // Initialize priority mapping for CREATE_JIRA_TASK actions
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.CREATE_JIRA_TASK) {
      if (!this.step.data.action.data.priorityMapping) {
        this.step.data.action.data.priorityMapping = {};
      }
    }

    // Load code if it's a trigger function
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.TRIGGER_FUNCTION) {
      this.code = this.step.data.action.data.code;
    }

    // Load conversation titles for existing conversation links
    await this.loadConversationTitles();

    // Initialize merge field emojis for CREATE_CONVERSATION actions
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION) {
      await this.initializeMergeFieldEmojis();
    }

    // Load channel names for any action type with channelId
    await this.loadChannelNames();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the step data OR the lookups change externally, re-initialize
    if (changes['step'] || changes['lookups']) {
      // Only proceed if the step type is relevant
      if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD) {
        // Avoid running hydration if the selected field hasn't changed and lookups haven't significantly changed (simple check)
        // This prevents potential loops if hydration itself causes further changes down the line.
        const lookupsChanged =
          changes['lookups'] && changes['lookups'].previousValue !== changes['lookups'].currentValue;
        const stepChanged = changes['step'] && changes['step'].previousValue !== changes['step'].currentValue;

        // Re-hydrate if the step changed OR if lookups changed (and we have a field selected)
        if (stepChanged || (lookupsChanged && this.selectedCustomFieldForAction)) {
          this.initializeSetCustomFieldState();
        }
      }

      // Load conversation titles when step changes (for CREATE_CONVERSATION actions)
      if (changes['step'] && this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION) {
        this.loadConversationTitles();
      }

      // Load channel names when step changes for any action type with channelId
      if (changes['step']) {
        this.loadChannelNames();
      }
    }
  }

  // Initialize state based on current step data
  async initializeSetCustomFieldState() {
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD) {
      const fieldId = this.step.data.action.data.customFieldId;
      if (fieldId) {
        const field = await this.customFieldsService.getById(fieldId);
        this.selectedCustomFieldForAction = field;
        // Hydrate the value for the child input component
        this.hydrateValueForInput();
      } else {
        this.selectedCustomFieldForAction = null;
        this.hydratedCustomFieldValue = null;
      }
      this.changeDetectorRef.markForCheck(); // Ensure UI updates if needed
    }
  }

  // Hydrates the value stored as ID(s) into full object(s) for the child input
  private hydrateValueForInput(): void {
    const storedValue = this.step?.data?.action?.data?.value;
    const field = this.selectedCustomFieldForAction;

    if (!field || storedValue === null || storedValue === undefined) {
      this.hydratedCustomFieldValue = storedValue; // Pass null/undefined/primitives directly
      return;
    }

    const hydrate = (id: string): any => {
      const stringId = String(id); // Ensure string key for lookups
      switch (field.type) {
        case CUSTOM_FIELD_TYPES.USER_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_USER_SELECT:
          return { id: stringId, name: this.lookups['assigneeId']?.[stringId] || stringId };
        case CUSTOM_FIELD_TYPES.USER_GROUP_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT:
          const name = this.lookups['groupId']?.[stringId] ?? this.lookups['userGroupId']?.[stringId] ?? stringId;
          return { id: stringId, name: name };
        case CUSTOM_FIELD_TYPES.SINGLE_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_SELECT:
          if (field.externalSelect) {
            // For external, we need {value, label}
            // Attempt to find label in lookups (might be populated by edit-automation)
            const label = this.lookups['externalSelect']?.[stringId] ?? stringId;
            return { value: stringId, label: label };
          } else {
            // Internal select stores primitive value directly
            return id; // Return the primitive value itself
          }
        default:
          return id; // Return primitive for other types (text, date, checkbox)
      }
    };

    if (Array.isArray(storedValue)) {
      this.hydratedCustomFieldValue = storedValue.map(hydrate).filter((v) => v !== null && v !== undefined); // Hydrate each ID in array
    } else {
      this.hydratedCustomFieldValue = hydrate(storedValue); // Hydrate single ID
    }
  }

  async loadPriorityLabels() {
    const resolvedTokens = await this.tokenService.getResolvedTokens(['PRIORITY_LABEL_MAP'], undefined);
    this.priorityLabels = resolvedTokens['PRIORITY_LABEL_MAP'] || DEFAULT_PRIORITY_TOKENS;
    this.priorityOptionsForTemplate = getOrderedPriorityOptions(this.priorityLabels);
    this.priorityValueToTokenMap = createPriorityValueToTokenMap(this.priorityLabels);
  }

  selectAssignee(actionData: any) {
    this.searchablePopoverService.assignees({
      event,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: false,
      selectedId: actionData.entityId,
      callback: async (id: string, _name: string, object: any) => {
        actionData.entityId = id;
        actionData.entityType =
          object.type !== SUPPORT_STEP_ASSIGNEE_TYPES.USER
            ? SUPPORT_STEP_ASSIGNEE_TYPES.GROUP
            : SUPPORT_STEP_ASSIGNEE_TYPES.USER;

        // Keep track in lookups
        if (!this.lookups.assigneeId) {
          this.lookups.assigneeId = {};
        }
        if (!this.lookups.groupId) {
          this.lookups.groupId = {};
        }
        if (!this.lookups.userGroupId) {
          this.lookups.userGroupId = {};
        }

        if (actionData.entityType === SUPPORT_STEP_ASSIGNEE_TYPES.USER) {
          this.lookups.assigneeId[id] = object.name;
        } else {
          this.lookups.groupId[id] = object.name;
          this.lookups.userGroupId[id] = object.name;
        }
      },
    });
  }

  selectSubStatus(actionData: any) {
    this.searchablePopoverService.subStatusKeys({
      event,
      projectId: this.projectId,
      callback: async (id: string, name: string) => {
        actionData.subStatusKey = id;

        if (!this.lookups.subStatusKey) {
          this.lookups.subStatusKey = {};
        }

        this.lookups.subStatusKey[id] = name;
      },
    });
  }

  getSubStatusIcon(subStatusKey: string) {
    const type = subStatusKey.split('__')[0];

    return STATUS_COLUMN_FORMAT_MAP[type]?.icon;
  }

  getSubStatusIconClass(subStatusKey: string) {
    const type = subStatusKey.split('__')[0];

    return STATUS_COLUMN_FORMAT_MAP[type]?.iconClass;
  }

  selectTag(actionData: any) {
    this.searchablePopoverService.tags({
      event,
      projectId: this.projectId ?? undefined,
      callback: async (id: string, _name: string, object: any) => {
        const list = actionData.tagIds || [];
        if (list.includes(id)) {
          return;
        }

        actionData.tagIds = [...list, id];

        if (!this.lookups.tagId) {
          this.lookups.tagId = {};
        }

        this.lookups.tagId[id] = object.name;
      },
    });
  }

  removeTag(actionData: any, index: number) {
    if (actionData.tagIds) {
      actionData.tagIds.splice(index, 1);
    }
  }

  selectTicketType(actionData: any) {
    this.searchablePopoverService.ticketTypes({
      event,
      projectId: this.projectId,
      callback: async (id: string, _name: string, object: any) => {
        actionData.ticketTypeId = id;

        if (!this.lookups.ticketTypeId) {
          this.lookups.ticketTypeId = {};
        }

        this.lookups.ticketTypeId[id] = object.name;
      },
    });
  }

  selectCollaboratorType(actionData: any) {
    this.searchablePopoverService.collaboratorTypes({
      event,
      callback: async (id: string, name: string, object: any) => {
        actionData.collaboratorTypeId = id;

        if (!this.lookups.collaboratorTypeId) {
          this.lookups.collaboratorTypeId = {};
        }

        this.lookups.collaboratorTypeId[id] = name;
      },
    });
  }

  selectSlackUserGroup(actionData: any) {
    this.searchablePopoverService.slackUserGroups({
      event,
      callback: async (id: string, _name: string, object: any) => {
        actionData.slackUserGroupId = id;

        if (!this.lookups.slackGroupId) {
          this.lookups.slackGroupId = {};
        }

        this.lookups.slackGroupId[id] = object.name;
      },
    });
  }

  selectUserForList(actionData: any) {
    this.searchablePopoverService.users({
      event,
      showAssignToMeText: '',
      showNoneOption: false,
      callback: async (id: string, _name: string, object: any) => {
        if (actionData.userIds.includes(id)) {
          return;
        }

        actionData.userIds.push(id);

        if (!this.lookups.assigneeId) {
          this.lookups.assigneeId = {};
        }

        this.lookups.assigneeId[id] = object.name;
      },
    });
  }

  selectTemplate(actionData: any) {
    this.searchablePopoverService.templates({
      event,
      showNoneOption: true,
      selectedTemplateId: actionData.templateId,
      projectId: this.projectId ?? undefined,
      callback: (id: string, _title: string, data: any) => {
        actionData.templateId = id;

        if (!this.lookups.templateId) {
          this.lookups.templateId = {};
        }

        if (!this.lookups.templateHtml) {
          this.lookups.templateHtml = {};
        }

        this.lookups.templateId[id] = data.name;
        this.lookups.templateHtml[id] = data.html;
      },
    });
  }

  selectSlackChannel(actionData: any) {
    this.searchablePopoverService.slackChannels({
      event,
      callback: async (id: string, _name: string, object: any) => {
        actionData.channelId = id;

        if (!this.lookups.slackChannelId) {
          this.lookups.slackChannelId = {};
        }

        this.lookups.slackChannelId[id] = object.name;
      },
    });
  }

  onSlackChannelChange(channel: SlackChannel) {
    this.step.data.action.data.channelId = channel.id;
    this.lookups.slackChannelId[channel.id] = channel.name;
  }

  removeUserFromList(actionData: any, index: number) {
    actionData.userIds.splice(index, 1);
  }

  // If your action is "triggerFunction" and you had an Edit Functions modal:
  async editFunctions() {
    const customActionsVersion = this.getFunctionsVersion('draft') ?? this.getFunctionsVersion('active');

    const modal = await this.modalCtrl.create({
      component: EditAutomationFunctionsComponent,
      cssClass: 'modal-full-screen',
      componentProps: {
        version: customActionsVersion.version,
        status: customActionsVersion.status,
        projectId: this.projectId,
      },
      keyboardClose: false,
      backdropDismiss: false,
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getFunctionsVersions();
    });
  }

  async getFunctionsVersions() {
    try {
      const functionsVersionsResponse: any = await this.automationsService.getAllFunctionsVersions(this.projectId);

      this.functionsVersions = functionsVersionsResponse;
      this.functionNames = this.getFunctionsVersion('active').exportedFunctionsByType.automationAction;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  getFunctionsVersion(status: string) {
    return (this.functionsVersions ?? []).find((version) => version.status === status);
  }

  handleActionChange() {
    // Could pass the entire action object to parent if needed
    this.actionDidChange.emit();
  }

  async handleOptionsClick() {
    const options: ActionDropdownType[] = [
      {
        label: 'Remove',
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    // Someday:
    // {
    //   label: 'Move up',
    //   icon: 'arrow-up-outline',
    // },
    // {
    //   label: 'Move down',
    //   icon: 'arrow-down-outline',
    // },

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number, label: string) => {
          popover.dismiss();

          this.remove.emit();
        },
      },
      event,
    });
    popover.present();
  }

  toggleWorkingTimeOnly(actionData: any) {
    actionData.wait.workingTimeOnly = !actionData.wait.workingTimeOnly;
  }

  toggleCreateTemporaryTriageChannel(actionData: any) {
    if (!actionData.createChannel) {
      actionData.createChannel = {
        prefix: 'triage-',
        isPrivate: false,
      };
      actionData.channelId = undefined;
    } else {
      actionData.createChannel = undefined;
    }
  }

  compareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  selectResolvableEntity(
    actionData: any,
    fieldId: string,
    fieldTypeOrEntityType?: string | typeof ENTITY_TYPES.USER | typeof ENTITY_TYPES.USER_GROUP,
  ) {
    const selectedValue = actionData[fieldId];

    const isEntityType =
      fieldTypeOrEntityType === ENTITY_TYPES.USER || fieldTypeOrEntityType === ENTITY_TYPES.USER_GROUP;
    const isFieldType =
      fieldTypeOrEntityType && Object.values(CUSTOM_FIELD_TYPES).includes(fieldTypeOrEntityType as any);

    // For backwards compatibility with entity types, convert to field type
    let fieldType: string | undefined;
    if (isEntityType) {
      fieldType =
        fieldTypeOrEntityType === ENTITY_TYPES.USER
          ? CUSTOM_FIELD_TYPES.USER_SELECT
          : CUSTOM_FIELD_TYPES.USER_GROUP_SELECT;
    } else if (isFieldType) {
      fieldType = fieldTypeOrEntityType;
    }

    this.searchablePopoverService.showPopover({
      event,
      showImages: false,
      showSearch: true,
      selectedItemId:
        selectedValue === null
          ? null
          : typeof selectedValue === 'object' && 'valueFrom' in selectedValue && selectedValue.valueFrom
            ? selectedValue.valueFrom
            : typeof selectedValue === 'object' && 'value' in selectedValue && selectedValue.value
              ? ''
              : null,
      callback: (id: string, name: string, object: any) => {
        if (id === '') {
          // Set appropriate default value based on field type
          let defaultValue: any = '';

          if (typeof fieldTypeOrEntityType === 'string') {
            switch (fieldTypeOrEntityType) {
              case CUSTOM_FIELD_TYPES.CHECKBOX:
                defaultValue = false;
                break;
              case CUSTOM_FIELD_TYPES.DATE:
                defaultValue = null;
                break;
              case CUSTOM_FIELD_TYPES.MULTI_SELECT:
              case CUSTOM_FIELD_TYPES.MULTI_USER_SELECT:
              case CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT:
                defaultValue = [];
                break;
              default:
                defaultValue = '';
                break;
            }
          }

          actionData[fieldId] = {
            value: defaultValue,
          };
          // For SET_CUSTOM_FIELD actions, also extract the value for proper storage
          if (fieldId === 'value' && this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD) {
            actionData[fieldId] = this.extractValueForStorage(actionData[fieldId]);
          }
        } else if (id === null || id === actionData[fieldId]?.valueFrom || id === actionData[fieldId]?.value) {
          actionData[fieldId] = null;
        } else {
          actionData[fieldId] = object.isValue
            ? {
                value: id,
              }
            : {
                valueFrom: id,
              };

          if (id.startsWith('conversation.ticketTypeFields[')) {
            const ticketTypeFieldId = extractTicketId(id);

            if (!this.lookups.customFieldId) {
              this.lookups.customFieldId = {};
            }

            this.lookups.customFieldId[ticketTypeFieldId] = name;
          }
        }

        if (this.showSpecificEntitySelection(actionData, fieldId)) {
          this.selectSpecificEntity(actionData, fieldId, fieldTypeOrEntityType);
        }
      },
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();
        const options: any[] = [];

        // Add "Specific Value" option first
        const specificValueLabels = {
          [CUSTOM_FIELD_TYPES.SHORT_ANSWER]: 'Choose a Text Value...',
          [CUSTOM_FIELD_TYPES.LONG_ANSWER]: 'Choose a Text Value...',
          [CUSTOM_FIELD_TYPES.TEXT]: 'Choose a Text Value...',
          [CUSTOM_FIELD_TYPES.SINGLE_SELECT]: 'Choose an Option...',
          [CUSTOM_FIELD_TYPES.CHECKBOX]: 'Choose a True/False Value...',
          [CUSTOM_FIELD_TYPES.DATE]: 'Choose a Date...',
          [CUSTOM_FIELD_TYPES.MULTI_SELECT]: 'Choose Options...',
          [CUSTOM_FIELD_TYPES.USER_SELECT]: 'Choose a User...',
          [CUSTOM_FIELD_TYPES.USER_GROUP_SELECT]: 'Choose a User Group...',
        };

        const defaultLabel = isEntityType
          ? fieldTypeOrEntityType === ENTITY_TYPES.USER
            ? 'Choose a User...'
            : 'Choose a User Group...'
          : 'Choose a Value...';

        // Add "Specific Value" option at the beginning
        options.unshift({
          title: specificValueLabels[fieldType as any] || defaultLabel,
          id: '',
          object: {},
          icon: 'person-circle',
        });

        // Handle backwards compatibility - if no field type specified, show standard user/user-group fields
        if (!fieldType) {
          options.push(...STANDARD_RESOLVABLE_ENTITY_FIELDS);
          return options.filter((f) => !cleanQuery || f.title.toLowerCase().includes(cleanQuery.toLowerCase()));
        }

        // Add type-specific resolvable fields
        if ([CUSTOM_FIELD_TYPES.USER_SELECT, CUSTOM_FIELD_TYPES.USER_GROUP_SELECT].includes(fieldType as any)) {
          options.push(...STANDARD_RESOLVABLE_ENTITY_FIELDS);
        }

        // Add custom fields of the same type
        try {
          const customFieldsResponse = (
            await this.customFieldsService.list({
              limit: null,
              order: ['name'],
              descending: false,
              where: [
                {
                  field: 'type',
                  operator: 'in',
                  value: [fieldType],
                },
              ],
            })
          ).data as CustomField[];
          customFieldsResponse.map((f) => {
            options.push({
              title: f.label,
              id: `conversation.ticketTypeFields['${f.id}']`,
              object: {},
              icon: 'flask',
            });
          });
        } catch (error) {
          console.warn('Failed to load custom fields for automation:', error);
        }

        // Add automation input fields of the same type (only for custom field types)
        if (isFieldType) {
          options.push(
            ...this.inputFields
              .filter((f) => f.type === fieldType)
              .map((f) => ({
                title: f.label,
                id: `customMap['${f.id}']`,
                object: {},
                icon: 'flask',
              })),
          );
        } else if (isEntityType) {
          // For backwards compatibility with entity types, filter by compatible input fields
          const compatibleInputTypes: string[] =
            fieldTypeOrEntityType === ENTITY_TYPES.USER
              ? [CUSTOM_FIELD_TYPES.USER_SELECT]
              : [CUSTOM_FIELD_TYPES.USER_GROUP_SELECT];

          options.push(
            ...this.inputFields
              .filter((f) => compatibleInputTypes.includes(f.type))
              .map((f) => ({
                title: f.label,
                id: `customMap['${f.id}']`,
                object: {
                  type: f.type === CUSTOM_FIELD_TYPES.USER_SELECT ? ENTITY_TYPES.USER : ENTITY_TYPES.USER_GROUP,
                },
                icon: 'flask',
                note: 'Automation Input Field',
              })),
          );
        }

        // Ensure we always have at least the "Specific Value" option
        if (options.length === 1 && options[0].id === '') {
          // If we only have the "Specific Value" option, it means no context fields were added
          // This could happen for unknown field types - add a basic fallback
          if (fieldType && !specificValueLabels[fieldType as any]) {
            options[0].title = 'Choose a Value...';
          }
        }

        return options.filter((f) => !cleanQuery || f.title.toLowerCase().includes(cleanQuery.toLowerCase()));
      },
    });
  }

  selectSpecificEntity(
    actionData: any,
    fieldId: string,
    fieldTypeOrEntityType?: string | typeof ENTITY_TYPES.USER | typeof ENTITY_TYPES.USER_GROUP,
    requireSlackId = false,
  ) {
    // Handle backwards compatibility with entity types
    const isEntityType =
      fieldTypeOrEntityType === ENTITY_TYPES.USER || fieldTypeOrEntityType === ENTITY_TYPES.USER_GROUP;
    const isUserOrGroupField =
      fieldTypeOrEntityType === CUSTOM_FIELD_TYPES.USER_SELECT ||
      fieldTypeOrEntityType === CUSTOM_FIELD_TYPES.USER_GROUP_SELECT ||
      isEntityType;

    // For user/user-group types, use the existing entities popover
    if (isUserOrGroupField) {
      let entityType: typeof ENTITY_TYPES.USER | typeof ENTITY_TYPES.USER_GROUP;

      if (isEntityType) {
        // Backwards compatibility: direct entity type
        entityType = fieldTypeOrEntityType as typeof ENTITY_TYPES.USER | typeof ENTITY_TYPES.USER_GROUP;
      } else {
        // Convert field type to entity type
        entityType =
          fieldTypeOrEntityType === CUSTOM_FIELD_TYPES.USER_SELECT ? ENTITY_TYPES.USER : ENTITY_TYPES.USER_GROUP;
      }

      this.searchablePopoverService.entities({
        type: entityType,
        selectedEntityId:
          actionData[fieldId] && typeof actionData[fieldId] === 'object' && 'value' in actionData[fieldId]
            ? actionData[fieldId].value
            : actionData[fieldId],
        requireSlackId,
        showNoneOption: true,
        callback: (id: string, name: string, _object: any) => {
          actionData[fieldId] = {
            value: id,
          };

          // For SET_CUSTOM_FIELD actions, also extract the value for proper storage
          if (fieldId === 'value' && this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD) {
            actionData[fieldId] = this.extractValueForStorage(actionData[fieldId]);
          }

          // Update the appropriate lookup table based on entity type
          if (entityType === ENTITY_TYPES.USER) {
            if (!this.lookups.assigneeId) {
              this.lookups.assigneeId = {};
            }
            this.lookups.assigneeId[id] = name;
          } else if (entityType === ENTITY_TYPES.USER_GROUP) {
            if (!this.lookups.groupId) {
              this.lookups.groupId = {};
            }
            if (!this.lookups.userGroupId) {
              this.lookups.userGroupId = {};
            }
            this.lookups.groupId[id] = name;
            this.lookups.userGroupId[id] = name;
          }
        },
      });
    } else {
      // For other field types, we'll use the custom field input component
      // This is handled by the existing AutomationCustomFieldInputComponent
      // Set appropriate default value based on field type
      let defaultValue: any = '';

      if (typeof fieldTypeOrEntityType === 'string') {
        switch (fieldTypeOrEntityType) {
          case CUSTOM_FIELD_TYPES.CHECKBOX:
            defaultValue = false;
            break;
          case CUSTOM_FIELD_TYPES.DATE:
            defaultValue = null;
            break;
          case CUSTOM_FIELD_TYPES.MULTI_SELECT:
          case CUSTOM_FIELD_TYPES.MULTI_USER_SELECT:
          case CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT:
            defaultValue = [];
            break;
          default:
            defaultValue = '';
            break;
        }
      }

      actionData[fieldId] = {
        value: defaultValue,
      };

      // For SET_CUSTOM_FIELD actions, also extract the value for proper storage
      if (fieldId === 'value' && this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD) {
        actionData[fieldId] = this.extractValueForStorage(actionData[fieldId]);
      }
    }
  }

  getResolvableEntityLabel(
    resolvableEntity: any,
    fieldTypeOrEntityType?: string | typeof ENTITY_TYPES.USER | typeof ENTITY_TYPES.USER_GROUP,
  ) {
    const id =
      typeof resolvableEntity === 'object' && 'valueFrom' in resolvableEntity && resolvableEntity.valueFrom
        ? resolvableEntity.valueFrom
        : typeof resolvableEntity === 'object' && 'value' in resolvableEntity && resolvableEntity.value
          ? resolvableEntity.value
          : '';

    if (id.startsWith('conversation.ticketTypeFields[')) {
      const ticketTypeFieldId = extractTicketId(id);

      return this.lookups.customFieldId?.[ticketTypeFieldId];
    }

    if (id.startsWith('customMap[')) {
      const inputFieldId = extractTicketId(id);

      return this.inputFields.find((f) => f.id === inputFieldId)?.label;
    }

    // Check for standard context field labels
    const contextFieldLabels = {
      'conversation.title': 'Conversation Title',
      'conversation.status': 'Conversation Status',
      'conversation.isNew': 'Conversation Is New',
      'conversation.createdAt': 'Conversation Created Date',
      'conversation.updatedAt': 'Conversation Updated Date',
      'conversation.closedAt': 'Conversation Closed Date',
      'conversation.tagId': 'Conversation Tags',
      'conversation.customerTagId': 'Customer Tags',
      'conversation.accountTagId': 'Account Tags',
    };

    if (contextFieldLabels[id]) {
      return contextFieldLabels[id];
    }

    // Check for standard resolvable entity fields (for user/user-group types)
    const standardField = STANDARD_RESOLVABLE_ENTITY_FIELDS.find((f) => f.id === id);
    if (standardField) {
      return standardField.title;
    }

    // Handle backwards compatibility with entity types
    const isEntityType =
      fieldTypeOrEntityType === ENTITY_TYPES.USER || fieldTypeOrEntityType === ENTITY_TYPES.USER_GROUP;

    if (isEntityType) {
      return fieldTypeOrEntityType === ENTITY_TYPES.USER ? 'Specific User' : 'Specific User Group';
    }

    // Default to specific value labels by field type
    const specificValueLabels = {
      [CUSTOM_FIELD_TYPES.SHORT_ANSWER]: 'Specific Text Value',
      [CUSTOM_FIELD_TYPES.LONG_ANSWER]: 'Specific Text Value',
      [CUSTOM_FIELD_TYPES.TEXT]: 'Specific Text Value',
      [CUSTOM_FIELD_TYPES.SINGLE_SELECT]: 'Specific Option',
      [CUSTOM_FIELD_TYPES.CHECKBOX]: 'Specific True/False Value',
      [CUSTOM_FIELD_TYPES.DATE]: 'Specific Date',
      [CUSTOM_FIELD_TYPES.MULTI_SELECT]: 'Specific Options',
      [CUSTOM_FIELD_TYPES.USER_SELECT]: 'Specific User',
      [CUSTOM_FIELD_TYPES.USER_GROUP_SELECT]: 'Specific User Group',
    };

    return specificValueLabels[fieldTypeOrEntityType as any] || 'Specific User or User Group';
  }

  getValueForDisplayLookup(value: any, fieldType?: string): string | null {
    // Extract the actual ID from both wrapper object format and primitive format
    const id = typeof value === 'object' && value !== null && 'value' in value ? value.value : value;

    if (!id || typeof id !== 'string') {
      return null;
    }

    // Try different lookup tables based on field type
    if (fieldType === CUSTOM_FIELD_TYPES.USER_SELECT || fieldType === CUSTOM_FIELD_TYPES.MULTI_USER_SELECT) {
      return this.lookups?.['assigneeId']?.[id] || null;
    }

    if (
      fieldType === CUSTOM_FIELD_TYPES.USER_GROUP_SELECT ||
      fieldType === CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT
    ) {
      return this.lookups?.['groupId']?.[id] || this.lookups?.['userGroupId']?.[id] || null;
    }

    return null;
  }

  isEmptyEntityType(value: any) {
    return typeof value === 'string' || value?.value === '';
  }

  getResolvableStringLabel(resolvableValue: any, defaultLabel: string = 'None') {
    if (!resolvableValue) return defaultLabel;

    // If it's a merge field object
    if (typeof resolvableValue === 'object' && 'valueFrom' in resolvableValue && resolvableValue.valueFrom) {
      const id = resolvableValue.valueFrom;

      if (id.startsWith('conversation.ticketTypeFields[')) {
        const ticketTypeFieldId = extractTicketId(id);
        return this.lookups.customFieldId?.[ticketTypeFieldId] || `Ticket Field: ${ticketTypeFieldId}`;
      }

      if (id.startsWith('customMap[')) {
        const inputFieldId = extractTicketId(id);
        return this.inputFields.find((f) => f.id === inputFieldId)?.label || `Input: ${inputFieldId}`;
      }

      return STANDARD_RESOLVABLE_STRING_FIELDS.find((f) => f.id === id)?.title || id;
    }

    // If it's a custom value object
    if (typeof resolvableValue === 'object' && 'value' in resolvableValue && resolvableValue.value) {
      return 'Custom Value';
    }

    // If it's a direct value, try to get the lookup
    return resolvableValue;
  }

  onTitleChange(actionData: any, content: string) {
    if (!actionData.title) {
      actionData.title = {};
    }
    actionData.title.value = content;
  }

  onIssueDetailsChange(actionData: any, content: string) {
    if (!actionData.description) {
      actionData.description = {};
    }
    actionData.description.value = content;
  }

  showSpecificEntitySelection(actionData: any, fieldId: string) {
    // If no custom field is selected, always show the resolvable selector (disabled dropdown)
    if (!this.selectedCustomFieldForAction) {
      return false;
    }

    const fieldValue = actionData[fieldId];

    // Check if a specific value is set
    // Value is considered "specific" if:
    // 1. It's a primitive value (string, number, boolean) - legacy format
    // 2. It's an object with 'value' property (new format for specific values)
    // Value is considered "resolvable" if it's an object with 'valueFrom' property

    if (fieldValue === null || fieldValue === undefined) {
      return false; // No value set
    }

    if (typeof fieldValue !== 'object') {
      return true; // Primitive value (legacy format)
    }

    // It's an object - check if it's a specific value or resolvable value
    if ('valueFrom' in fieldValue) {
      return false; // Resolvable value
    }

    if ('value' in fieldValue) {
      // Special case: Primary/Secondary Support Assignee are resolvable keywords stored as {value: 'keyword'}
      const specialResolvableKeywords = [
        SUPPORT_STEP_KEYWORDS.PRIMARY_SUPPORT_ASSIGNEE,
        SUPPORT_STEP_KEYWORDS.SECONDARY_SUPPORT_ASSIGNEE,
      ];

      if (specialResolvableKeywords.includes(fieldValue.value)) {
        return false; // These are resolvable keywords, not specific values
      }

      return true; // All other {value: X} objects are specific values
    }

    return true; // Default to specific for unknown object structures
  }

  async selectCustomField(data: any, event: any) {
    // Use the service to show a popover listing available fields
    this.searchablePopoverService.customFields({
      event,
      allowedTypes: [
        CUSTOM_FIELD_TYPES.CHECKBOX,
        CUSTOM_FIELD_TYPES.SINGLE_SELECT,
        CUSTOM_FIELD_TYPES.MULTI_SELECT,
        CUSTOM_FIELD_TYPES.DATE,
        CUSTOM_FIELD_TYPES.USER_SELECT,
        CUSTOM_FIELD_TYPES.MULTI_USER_SELECT,
        CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
        CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT,
      ],
      projectId: this.projectId,
      callback: (id: string, name: string, object: any) => {
        if (object) {
          // Check if the selected field is different from the current one
          if (data.customFieldId !== id) {
            data.customFieldId = id; // Update the ID in the step data
            this.selectedCustomFieldForAction = object; // Update the selected field object
            data.value = null; // Reset the value when the field changes
            this.hydratedCustomFieldValue = null; // Reset hydrated value too
            this.changeDetectorRef.markForCheck(); // Trigger UI update
          }
        }
      },
    });
  }

  // Handles the output event from the AutomationCustomFieldInputComponent
  onCustomFieldValueChange(newValue: any) {
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD) {
      // Update the hydrated value for immediate display feedback in the child
      this.hydratedCustomFieldValue = newValue;

      // Extract the ID(s) to save to the actual step data
      switch (this.selectedCustomFieldForAction?.type) {
        case CUSTOM_FIELD_TYPES.MULTI_USER_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT:
          // Ensure newValue is an array and map safely
          this.step.data.action.data.value = Array.isArray(newValue) ? newValue.map((v: any) => v?.id) : [];
          break;
        case CUSTOM_FIELD_TYPES.USER_SELECT:
        case CUSTOM_FIELD_TYPES.USER_GROUP_SELECT:
          this.step.data.action.data.value = newValue?.id;
          break;
        case CUSTOM_FIELD_TYPES.SINGLE_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_SELECT:
          if (this.selectedCustomFieldForAction?.externalSelect) {
            // External select emits {id, label} or array of them
            if (Array.isArray(newValue)) {
              this.step.data.action.data.value = newValue
                .map((v: any) => v?.id)
                .filter((v) => v !== null && v !== undefined);
            } else {
              // Get the 'value' property from the single object (remains null/undefined if newValue is null)
              this.step.data.action.data.value = newValue?.id;
            }
          } else {
            // Internal select emits the primitive value directly
            this.step.data.action.data.value = newValue;
          }
          break;
        default:
          // Includes text, date, boolean - newValue is the primitive
          this.step.data.action.data.value = newValue;
      }

      // Process the value to handle both resolvable and specific value structures
      this.step.data.action.data.value = this.extractValueForStorage(this.step.data.action.data.value);

      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Extracts the proper value for storage from various value structures:
   * - Resolvable values: {valueFrom: "path"} -> keep as-is for backend resolution
   * - Specific values: {value: actualValue} -> extract actualValue
   * - Primitive values -> keep as-is
   */
  private extractValueForStorage(value: any): any {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return value;
    }

    // Handle non-objects (primitives)
    if (typeof value !== 'object') {
      return value;
    }

    // Handle resolvable values - keep as-is for backend resolution
    if ('valueFrom' in value) {
      return value;
    }

    // Handle specific values - extract the actual value
    if ('value' in value) {
      return value.value;
    }

    // For any other object structure, return as-is
    return value;
  }

  async openEmojiPicker(ev: any) {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: async (emoji: any) => {
          popover.dismiss();
          this.step.data.action.data.emoji = emoji.id;
          this.hydratedEmojiValue = await this.emojiService.resolveEmojiToDisplay(emoji.id);
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event: ev,
    });

    await popover.present();
  }

  toggleAdvancedSettings() {
    this.showAdvancedSettings = !this.showAdvancedSettings;
  }

  selectAbortEvent(wait: any) {
    // Events to exclude from the list
    const includedEvents = [
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].CLOSED,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].PUT_ON_HOLD,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].REOPENED,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].MESSAGE_ADDED,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].INTERNAL_MESSAGE_ADDED,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].EXTERNAL_MESSAGE_ADDED,
    ] as string[];

    // Create the list of available events, filtering out excluded ones
    const eventOptions = Object.entries(AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION])
      .filter(([key, value]) => includedEvents.includes(value))
      .map(([key, value]) => ({
        id: value,
        title: this.formatEventLabel(value),
        objectType: AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION,
      }));

    this.searchablePopoverService.showPopover({
      event,
      showImages: false,
      showSearch: true,
      selectedItemId: null,
      callback: (eventId: string, _name: string, object: any) => {
        if (!wait.abortOn) {
          wait.abortOn = [];
        }

        // Use the hardcoded objectType since object is null
        const objectType = AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION;

        // Check if this event is already added
        const exists = wait.abortOn.some((item: any) => item.event === eventId && item.objectType === objectType);

        if (!exists) {
          wait.abortOn = [
            ...wait.abortOn,
            {
              event: eventId,
              objectType: objectType,
            },
          ];
        }
      },
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim().toLowerCase();

        return eventOptions.filter((option) => !cleanQuery || option.title.toLowerCase().includes(cleanQuery));
      },
    });
  }

  removeAbortEvent(wait: any, index: number) {
    if (wait.abortOn) {
      wait.abortOn = wait.abortOn.filter((_, i) => i !== index);
    }
  }

  formatAbortEventLabel(abortEvent: { event: string; objectType: string }): string {
    return this.formatEventLabel(abortEvent.event);
  }

  selectCustomer(actionData: any) {
    this.searchablePopoverService.customers({
      event,
      callback: async (id: string, _name: string, object: any) => {
        actionData.customerId = id;

        if (!this.lookups.customerId) {
          this.lookups.customerId = {};
        }

        this.lookups.customerId[id] = object.name;
      },
    });
  }

  selectProject(actionData: any) {
    this.searchablePopoverService.projects({
      event,
      callback: async (id: string, _name: string, object: any) => {
        actionData.projectId = id;

        if (!this.lookups.projectId) {
          this.lookups.projectId = {};
        }

        this.lookups.projectId[id] = object.name;
      },
    });
  }

  private formatEventLabel(event: string): string {
    // Convert camelCase to readable format
    const formatted = event
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    // Special cases for better readability
    const replacements: Record<string, string> = {
      'Internal Message Added': 'Agent Message Added',
      'External Message Added': 'Customer Message Added',
      'Internal Unresponsive': 'Agent Unresponsive',
      'External Unresponsive': 'Customer Unresponsive',
    };

    return replacements[formatted] || formatted;
  }

  // Jira Task Creation Methods
  selectJiraProject(actionData: any) {
    this.searchablePopoverService.showPopover({
      event,
      showImages: false,
      showSearch: true,
      selectedItemId: actionData.projectKey || null,
      callback: (id: string, _name: string, _object: any) => {
        actionData.projectKey = id || null;
      },
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();

        // Get available Jira projects from the tenant's Jira integration
        const jiraConfig = this.authService.tenant.integrations?.jira;

        if (!jiraConfig?.enabled) {
          return [
            {
              title: 'Jira integration not configured',
              id: null,
              object: {},
            },
          ];
        }

        // The projectKeys are stored in the params object from the integration registration
        const projectKeys = jiraConfig.userInput?.projectKeys?.split(',').map((key) => key.trim()) || [];

        const options = projectKeys.map((key) => ({
          title: key,
          id: key,
          object: {},
        }));

        return options.filter((f) => !cleanQuery || f.title.toLowerCase().includes(cleanQuery.toLowerCase()));
      },
    });
  }

  async initializeMergeFieldEmojis() {
    const actionData = this.step?.data?.action?.data;
    if (!actionData) return;

    // Initialize emojis for priority merge fields
    if (actionData.priority && typeof actionData.priority === 'object' && 'valueFrom' in actionData.priority) {
      if (actionData.priority.valueFrom === 'conversation.priority' && !actionData.priority._resolvedEmoji) {
        const mergeFieldEmoji = ':zap:';
        const resolvedEmoji = await this.emojiService.resolveEmojiToDisplay(mergeFieldEmoji.replace(/:/g, ''));
        actionData.priority._resolvedEmoji = resolvedEmoji;
      }
    }

    // Can add other merge field emoji initializations here as needed
    // if (actionData.status && typeof actionData.status === 'object' && 'valueFrom' in actionData.status) { ... }
  }

  // Load channel names for automation actions with saved channel IDs
  async loadChannelNames() {
    const actionType = this.step?.data?.action?.type;
    const actionData = this.step?.data?.action?.data;

    // Check if this action type has a channelId field
    const hasChannelId =
      actionType === AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION ||
      actionType === AUTOMATION_ACTION_TYPES.POST_TO_TRIAGE;

    if (!hasChannelId || !actionData?.channelId) {
      return;
    }

    // Extract channel ID based on action type format
    let channelId: string | null = null;

    if (actionType === AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION) {
      // CREATE_CONVERSATION uses resolvable values: { value: "C1234567890" }
      if (typeof actionData.channelId === 'object' && 'value' in actionData.channelId) {
        channelId = actionData.channelId.value;
      } else if (typeof actionData.channelId === 'string') {
        channelId = actionData.channelId;
      }

      // Skip merge fields for CREATE_CONVERSATION
      if (typeof actionData.channelId === 'object' && 'valueFrom' in actionData.channelId) {
        return;
      }
    } else if (actionType === AUTOMATION_ACTION_TYPES.POST_TO_TRIAGE) {
      // POST_TO_TRIAGE uses simple strings: "C1234567890"
      if (typeof actionData.channelId === 'string') {
        channelId = actionData.channelId;
      }
    }

    // Skip if no valid channel ID
    if (!channelId) {
      return;
    }

    // Initialize lookups if needed
    if (!this.lookups) {
      this.lookups = {};
    }
    if (!this.lookups.slackChannelId) {
      this.lookups.slackChannelId = {};
    }

    // Skip if we already have the channel name cached
    if (this.lookups.slackChannelId[channelId]) {
      return;
    }

    try {
      // Fetch the channel details using the same pattern as other components
      const channel = await this.slackService.getChannel(channelId);
      if (channel) {
        this.lookups.slackChannelId[channelId] = channel.name;
        // Trigger change detection to update the UI
        this.changeDetectorRef.detectChanges();
      }
    } catch (error) {
      console.warn('Failed to load channel name for ID:', channelId, error);
      // Don't throw - this is just for display enhancement
    }
  }

  // Generic merge field popover that combines static options with merge field support
  private showMergeFieldPopover({
    selectedValue,
    mergeFieldId,
    mergeFieldTitle,
    mergeFieldIcon,
    mergeFieldEmoji,
    staticOptionsLoader,
    searchableOptionsLoader,
    callback,
    showSearch = true,
    showImages = false,
  }: {
    selectedValue: any;
    mergeFieldId: string;
    mergeFieldTitle: string;
    mergeFieldIcon?: string;
    mergeFieldEmoji?: string;
    staticOptionsLoader?: () => Promise<any[]>;
    searchableOptionsLoader?: (searchText: string) => Promise<any[]>;
    callback: (value: any) => void;
    showSearch?: boolean;
    showImages?: boolean;
  }) {
    // Helper to get selected item ID
    const getSelectedId = (value: any): string | null => {
      if (value === null) return null;
      if (typeof value === 'object' && 'valueFrom' in value) return value.valueFrom;
      if (typeof value === 'object' && 'value' in value) return value.value;
      return value;
    };

    this.searchablePopoverService.showPopover({
      event,
      showImages,
      showSearch,
      selectedItemId: getSelectedId(selectedValue),
      callback: async (id: string, name: string, object: any) => {
        if (id === '') {
          // Custom value
          callback({ value: '' });
        } else if (id === null) {
          // None/null value
          callback(null);
        } else if (id === mergeFieldId) {
          // Merge field selection - include resolved emoji if provided
          const mergeFieldData: any = { valueFrom: id };
          if (mergeFieldEmoji) {
            // Resolve the merge field emoji
            let resolvedMergeFieldEmoji = mergeFieldEmoji;
            if (
              typeof mergeFieldEmoji === 'string' &&
              (mergeFieldEmoji.startsWith(':') || /^[a-zA-Z0-9_+-]+$/.test(mergeFieldEmoji))
            ) {
              resolvedMergeFieldEmoji = await this.emojiService.resolveEmojiToDisplay(
                mergeFieldEmoji.replace(/:/g, ''),
              );
            }
            mergeFieldData._resolvedEmoji = resolvedMergeFieldEmoji;
          }
          callback(mergeFieldData);
        } else {
          // Static option selection - pass through the original object for any additional data
          callback({ value: id, _originalObject: object });
        }
      },
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();

        // Load options using either searchable or static loader
        const staticOptions = searchableOptionsLoader
          ? await searchableOptionsLoader(cleanQuery || '')
          : await staticOptionsLoader!();

        // Combine with merge field and custom value options
        const mergeFieldOption: any = {
          id: mergeFieldId,
          title: mergeFieldTitle,
        };

        // Add icon or emoji if provided
        if (mergeFieldIcon) {
          mergeFieldOption.icon = mergeFieldIcon;
        }
        if (mergeFieldEmoji) {
          mergeFieldOption.emoji = mergeFieldEmoji;
        }

        const allOptions: any[] = [mergeFieldOption, ...staticOptions];

        // Filter merge field option by search text if provided
        // (searchable loader handles its own filtering for static options)
        return allOptions.filter(
          (option) => !cleanQuery || option.title.toLowerCase().includes(cleanQuery.toLowerCase()),
        );
      },
    });
  }

  // Create Conversation Methods
  selectConversationStatus(actionData: any) {
    this.showMergeFieldPopover({
      selectedValue: actionData.status,
      mergeFieldId: 'conversation.status',
      mergeFieldTitle: 'Triggering Conversation Status',
      mergeFieldIcon: 'flash-outline', // Icon representing dynamic/merge field
      staticOptionsLoader: async () => STATUS_OPTIONS.filter((status) => status.id !== CONVERSATION_STATUS.INTAKE),
      callback: (value) => {
        actionData.status = value;
      },
    });
  }

  selectConversationPriority(actionData: any) {
    this.showMergeFieldPopover({
      selectedValue: actionData.priority,
      mergeFieldId: 'conversation.priority',
      mergeFieldTitle: 'Triggering Conversation Priority',
      mergeFieldEmoji: ':zap:',
      staticOptionsLoader: async () => {
        // Load emojis first (same as searchable popover service)
        await this.emojiService.loadEmojis();

        // Get all priority entries
        const priorityEntries = Object.entries(this.priorityValueToTokenMap);

        // Step 1: Get all emoji identifiers that need resolution
        const emojiIdentifiersToResolve = priorityEntries.map(([_, token]) => token.EMOJI);

        // Step 2: Resolve all emojis in parallel
        const resolvedEmojis = await Promise.all(
          emojiIdentifiersToResolve.map((identifier) => {
            if (
              typeof identifier === 'string' &&
              (identifier.startsWith(':') || /^[a-zA-Z0-9_+-]+$/.test(identifier))
            ) {
              // If it looks like an ID (colon-wrapped or plain word for ID)
              return this.emojiService.resolveEmojiToDisplay(identifier.replace(/:/g, ''));
            }
            // If it's already resolved (e.g., native emoji), return as-is
            return Promise.resolve(identifier);
          }),
        );

        // Step 3: Map to options with resolved emojis
        return priorityEntries.map(([value, token], index) => ({
          id: value,
          title: token.LABEL,
          emoji: resolvedEmojis[index], // Use resolved emoji instead of raw identifier
        }));
      },
      callback: async (value) => {
        if (value && typeof value === 'object' && 'value' in value) {
          // Convert string to number for priority values
          const priorityValue = Number(value.value);
          // Get the priority token and resolve its emoji
          const priorityToken = this.priorityValueToTokenMap[priorityValue];
          let resolvedEmoji = '';

          if (priorityToken?.EMOJI) {
            const identifier = priorityToken.EMOJI;
            if (
              typeof identifier === 'string' &&
              (identifier.startsWith(':') || /^[a-zA-Z0-9_+-]+$/.test(identifier))
            ) {
              resolvedEmoji = await this.emojiService.resolveEmojiToDisplay(identifier.replace(/:/g, ''));
            } else {
              resolvedEmoji = identifier;
            }
          }

          actionData.priority = {
            value: priorityValue,
            _resolvedEmoji: resolvedEmoji, // Store resolved emoji directly
          };
        } else {
          actionData.priority = value;
        }
      },
    });
  }

  selectConversationProject(actionData: any) {
    this.showMergeFieldPopover({
      selectedValue: actionData.projectId,
      mergeFieldId: 'conversation.projectId',
      mergeFieldTitle: 'Triggering Conversation Project',
      searchableOptionsLoader: async (searchText: string) => {
        const where: any[] = [];

        if (searchText?.length) {
          where.push({
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          });
        }

        const response = await this.projectsService.list({
          where,
          limit: 100,
          order: ['name'],
          descending: false,
        });

        return response.data.map((project) => ({
          id: project.id,
          title: project.name,
          object: project,
        }));
      },
      callback: (value) => {
        actionData.projectId = value;
        // Update lookups if we have a static value with original object
        if (value && typeof value === 'object' && '_originalObject' in value) {
          if (!this.lookups.projectId) {
            this.lookups.projectId = {};
          }
          this.lookups.projectId[value.value] = value._originalObject.name;
        }
      },
      showSearch: true,
    });
  }

  selectConversationTicketType(actionData: any) {
    this.showMergeFieldPopover({
      selectedValue: actionData.ticketTypeId,
      mergeFieldId: 'conversation.ticketTypeId',
      mergeFieldTitle: 'Conversation Ticket Type',
      mergeFieldIcon: 'document-text-outline', // Document icon for ticket type
      searchableOptionsLoader: async (searchText: string) => {
        const where: any[] = [];

        // Filter by project if available
        const projectId = this.getProjectIdForTicketTypeFiltering(actionData);
        if (projectId) {
          where.push({
            field: 'projectId',
            operator: '==',
            value: projectId,
          });
        }

        const response = await this.ticketTypesService.list({
          where,
          limit: 100,
          order: ['name'],
          descending: false,
          search: searchText,
        });

        return response.data.map((ticketType) => ({
          id: ticketType.id,
          title: ticketType.name,
          object: ticketType,
        }));
      },
      callback: (value) => {
        const oldTicketTypeId = actionData.ticketTypeId;
        actionData.ticketTypeId = value;

        // Handle ticket type change side effects if we have a static value
        if (value && typeof value === 'object' && '_originalObject' in value) {
          this.handleTicketTypeChange(
            actionData,
            oldTicketTypeId,
            value.value,
            value._originalObject.name,
            value._originalObject,
          );

          // Update lookups for display
          if (!this.lookups.ticketTypeId) {
            this.lookups.ticketTypeId = {};
          }
          this.lookups.ticketTypeId[value.value] = value._originalObject.name;
        }
      },
      showSearch: true,
    });
  }

  private getProjectIdForTicketTypeFiltering(actionData: any): string | null {
    // Try to get project ID from the action data
    if (actionData.projectId) {
      if (typeof actionData.projectId === 'object' && 'value' in actionData.projectId) {
        return actionData.projectId.value;
      }
      if (typeof actionData.projectId === 'string') {
        return actionData.projectId;
      }
    }

    // Could also check for current context/route project if needed
    return null;
  }

  private handleTicketTypeChange(actionData: any, oldTicketTypeId: any, id: string, name: string, object: any) {
    // Clear cache if ticket type changed
    if (oldTicketTypeId !== id) {
      this.renderableFieldDefinitionsCache = {};
      // Reset custom field values when ticket type changes
      actionData.customFieldValues = {};
    }

    if (!this.lookups.ticketTypeId) {
      this.lookups.ticketTypeId = {};
    }
    this.lookups.ticketTypeId[id] = name;

    // Load field definitions from the ticket type object
    this.ticketTypeFieldDefinitions = (object?.fields as CustomField[]) || [];

    // Clear cache to force recalculation with new fields
    this.renderableFieldDefinitionsCache = {};
  }

  selectConversationSubmitter(actionData: any) {
    // Helper to get selected user ID
    const getSelectedUserId = (value: any): string | null => {
      if (value === null) return null;
      if (typeof value === 'object' && 'valueFrom' in value) return value.valueFrom;
      if (typeof value === 'object' && 'value' in value) return value.value;
      return value;
    };

    this.searchablePopoverService.showPopover({
      event,
      showImages: true,
      showSearch: true,
      selectedItemId: getSelectedUserId(actionData.submitterId),
      callback: async (id: string, name: string, object: any) => {
        console.log('selectConversationSubmitter callback:', { id, name, object });

        if (id === 'conversation.submitterUserId') {
          // Merge field selection
          actionData.submitterId = { valueFrom: id };
        } else if (id === null || id === '') {
          // None/null value
          actionData.submitterId = null;
        } else {
          // Static user selection
          actionData.submitterId = { value: id };

          // Update lookups for display
          if (!this.lookups.userId) {
            this.lookups.userId = {};
          }
          this.lookups.userId[id] = name;
        }
      },
      dynamicCreationCallback: async (text: string) => {
        // Create new user dynamically like in create conversation modal
        const newUser = await this.usersService.create(text);
        actionData.submitterId = { value: newUser.id };

        // Update lookups
        if (!this.lookups.userId) {
          this.lookups.userId = {};
        }
        this.lookups.userId[newUser.id] = newUser.name || newUser.email;
      },
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();

        // Create merge field option
        const mergeFieldOption = {
          id: 'conversation.submitterUserId',
          title: 'Conversation Submitter',
          icon: 'person-outline',
        };

        // Load users using the same logic as allUsers method
        const where: any[] = [
          {
            field: 'name',
            operator: '!=',
            value: null,
          },
        ];

        const result = await this.usersService.list({
          limit: 100,
          includeExternalUsers: true,
          where,
          order: ['name', 'email'],
          search: cleanQuery,
          descending: false,
        });

        const connectedTeams = await this.slackService.getConnectedTeams();

        const usersResponse = result.data.map((user) => {
          const name = user.name?.trim() || user.email?.trim() || 'No Name';
          const title = `${name}${connectedTeams?.length > 1 && user.slackTeam?.name ? ` (${user.slackTeam.name})` : ''}`;

          return {
            title,
            id: user.id,
            image: user.photo,
            object: user,
          };
        });

        const allOptions = [mergeFieldOption, ...usersResponse];

        // Filter merge field option by search text
        return allOptions.filter(
          (option) => !cleanQuery || option.title.toLowerCase().includes(cleanQuery.toLowerCase()),
        );
      },
    });
  }

  selectConversationDirectMessageUser(actionData: any) {
    this.searchablePopoverService.allUsers({
      event,
      selectedUserId: actionData.entityId,
      dynamicCreationCallback: async (text: string) => {
        const user = await this.usersService.create(text);
        actionData.entityId = user.id;

        // Update lookups for display
        if (!this.lookups.userId) {
          this.lookups.userId = {};
        }
        this.lookups.userId[user.id] = user.name || user.email || 'New User';
      },
      callback: async (id: string, name: string, user: any) => {
        actionData.entityId = id;

        // Update lookups for display
        if (!this.lookups.userId) {
          this.lookups.userId = {};
        }
        this.lookups.userId[id] = name;
      },
    });
  }

  getConversationDirectMessageUserLabel(entityId: any): string {
    return this.lookups?.userId?.[entityId] || 'Select User';
  }

  onConversationSendAsChange(actionData: any, value: string) {
    actionData.sendAs = value;
  }

  async loadUserForEntityId(entityId: string) {
    try {
      const user = await this.usersService.getUser(entityId);

      // Initialize lookups if needed
      if (!this.lookups.userId) {
        this.lookups.userId = {};
      }

      // Store user information in lookups
      this.lookups.userId[entityId] = user.name || user.email || 'Unknown User';
    } catch (error) {
      console.warn('Failed to load user for entityId:', entityId, error);
      // Set a fallback value
      if (!this.lookups.userId) {
        this.lookups.userId = {};
      }
      this.lookups.userId[entityId] = 'Unknown User';
    }
  }

  selectConversationCustomer(actionData: any) {
    this.showMergeFieldPopover({
      selectedValue: actionData.customerId,
      mergeFieldId: 'conversation.customerId',
      mergeFieldTitle: 'Conversation Customer',
      mergeFieldIcon: 'person-outline', // Person icon for customer
      staticOptionsLoader: async () => [], // For now, no static options - could load customers here
      callback: (value) => {
        actionData.customerId = value;
        // Update lookups if we have a static value with original object
        if (value && typeof value === 'object' && '_originalObject' in value) {
          if (!this.lookups.customerId) {
            this.lookups.customerId = {};
          }
          this.lookups.customerId[value.value] = value._originalObject.name;
        }
      },
    });
  }

  getConversationLinks(actionData: any): any[] {
    return actionData.conversationLinks || [];
  }

  removeConversationLink(actionData: any, linkToRemove: any) {
    if (actionData.conversationLinks) {
      const index = actionData.conversationLinks.findIndex((link: any) => link === linkToRemove);
      if (index !== -1) {
        actionData.conversationLinks.splice(index, 1);
      }
    }
  }

  getConversationLinkTypeLabel(type: string): string {
    const typeLabels: Record<string, string> = {
      [CONVERSATION_LINK_TYPES.RELATED_TO]: 'Related to conversation',
      [CONVERSATION_LINK_TYPES.BLOCKS]: 'Blocks conversation',
      [CONVERSATION_LINK_TYPES.TRACKS]: 'Tracks conversation',
    };
    return typeLabels[type] || type;
  }

  onConversationFieldValuesChange(actionData: any, fieldValues: any) {
    actionData.customFieldValues = fieldValues;

    // Clear cache when field values change to recalculate visible fields
    const ticketTypeId = actionData.ticketTypeId;
    if (ticketTypeId) {
      delete this.renderableFieldDefinitionsCache[ticketTypeId];
    }
  }

  async loadFieldDefinitions() {
    // Project field definitions will be loaded when project is selected
    // Ticket type field definitions will be loaded when ticket type is selected
    // For now, just clear the cache
    this.renderableFieldDefinitionsCache = {};
  }

  getConversationPostingType(actionData: any): 'channel' | 'direct' {
    // Default to channel if not specified
    return actionData.postingType || 'channel';
  }

  setConversationPostingType(actionData: any, type: 'channel' | 'direct') {
    actionData.postingType = type;

    // Clear channel selection when switching to direct message
    if (type === 'direct') {
      actionData.channelId = null;
      // Set default sendAs value if not already set
      if (!actionData.sendAs) {
        actionData.sendAs = 'support_team';
      }
    }
  }

  // Getter methods for displaying labels
  getConversationStatusValue(status: any): string | null {
    // Extract the actual status value from object format
    if (typeof status === 'object' && status) {
      if ('value' in status) return status.value;
      if ('valueFrom' in status) return status.valueFrom;
    }
    return status;
  }

  getConversationStatusLabel(status: any): string {
    // Handle merge fields
    if (typeof status === 'object' && 'valueFrom' in status) {
      return this.CONVERSATION_STATUS_LABELS[status.valueFrom] || 'Conversation Status';
    }
    // Handle static values
    if (typeof status === 'object' && 'value' in status) {
      return this.CONVERSATION_STATUS_LABELS[status.value] || 'Status';
    }
    // Handle direct values (legacy)
    return this.CONVERSATION_STATUS_LABELS[status] || 'Status';
  }

  getConversationPriorityIcon(priority: any): string {
    // Handle object format with stored resolved emoji
    if (typeof priority === 'object' && priority) {
      if ('_resolvedEmoji' in priority) {
        return priority._resolvedEmoji || '';
      }
      if ('value' in priority) {
        // Fallback to looking up by value
        const emoji = this.priorityValueToTokenMap[priority.value]?.EMOJI;
        return emoji && !emoji.startsWith(':') ? emoji : '';
      }
      if ('valueFrom' in priority) {
        // Handle merge field objects - return empty for now since we can't resolve them in display
        return '';
      }
    }
    // Handle direct values (legacy)
    if (!priority) return '';
    const emoji = this.priorityValueToTokenMap[priority]?.EMOJI;
    // Return empty string if emoji is still in :name: format (not yet resolved)
    return emoji && !emoji.startsWith(':') ? emoji : '';
  }

  getConversationPriorityLabel(priority: any): string {
    if (typeof priority === 'object' && 'valueFrom' in priority) {
      return this.getResolvableStringLabel(priority, 'Triggering Conversation Priority');
    }
    if (typeof priority === 'object' && 'value' in priority) {
      return this.priorityValueToTokenMap[priority.value]?.LABEL || 'Priority';
    }
    if (!priority) return 'Priority';
    return this.priorityValueToTokenMap[priority]?.LABEL || 'Priority';
  }

  getConversationProjectLabel(projectId: any): string {
    if (typeof projectId === 'object' && 'valueFrom' in projectId) {
      return this.getResolvableStringLabel(projectId, 'Triggering Conversation Project');
    }
    if (typeof projectId === 'object' && 'value' in projectId) {
      return this.lookups?.projectId?.[projectId.value] || 'Project';
    }
    return this.lookups?.projectId?.[projectId] || 'Project';
  }

  getConversationTicketTypeLabel(ticketTypeId: any): string {
    if (typeof ticketTypeId === 'object' && 'valueFrom' in ticketTypeId) {
      return this.getResolvableStringLabel(ticketTypeId, 'Conversation Ticket Type');
    }
    if (typeof ticketTypeId === 'object' && 'value' in ticketTypeId) {
      return this.lookups?.ticketTypeId?.[ticketTypeId.value] || 'None';
    }
    return this.lookups?.ticketTypeId?.[ticketTypeId] || 'None';
  }

  getConversationSubmitterLabel(submitterId: any): string {
    if (typeof submitterId === 'object' && 'valueFrom' in submitterId) {
      return this.getResolvableEntityLabel(submitterId, ENTITY_TYPES.USER);
    }
    if (typeof submitterId === 'object' && 'value' in submitterId) {
      return this.lookups?.userId?.[submitterId.value] || 'None';
    }
    return this.lookups?.userId?.[submitterId] || 'None';
  }

  getConversationCustomerLabel(customerId: any): string {
    if (typeof customerId === 'object' && 'valueFrom' in customerId) {
      return this.getResolvableStringLabel(customerId, 'Conversation Customer');
    }
    if (typeof customerId === 'object' && 'value' in customerId) {
      return this.lookups?.customerId?.[customerId.value] || 'None';
    }
    return this.lookups?.customerId?.[customerId] || 'None';
  }

  getConversationRenderableFieldDefinitions(ticketTypeId: any): any[] {
    // Extract actual ticket type ID from object format
    let actualTicketTypeId: string | null = null;
    if (typeof ticketTypeId === 'object' && ticketTypeId) {
      if ('value' in ticketTypeId) {
        actualTicketTypeId = ticketTypeId.value;
      } else if ('valueFrom' in ticketTypeId) {
        // For merge fields, we can't determine field definitions at design time
        return [];
      }
    } else {
      actualTicketTypeId = ticketTypeId;
    }

    if (!actualTicketTypeId) {
      return [];
    }

    // Use cache if available
    if (this.renderableFieldDefinitionsCache[actualTicketTypeId]) {
      return this.renderableFieldDefinitionsCache[actualTicketTypeId];
    }

    // Get ticket type field definitions for this specific ticket type
    // Note: ticketTypeFieldDefinitions should already be filtered for the current ticket type
    const ticketTypeFields = this.ticketTypeFieldDefinitions;

    // Get current field values for visibility logic
    const currentFieldValues = this.step?.data?.action?.data?.customFieldValues || {};

    // Get visible fields based on conditional visibility rules
    const visibleFields = getVisibleFields(ticketTypeFields, currentFieldValues);

    // Combine project fields with visible ticket type fields
    let renderableFields: CustomField[] = [...this.projectFieldDefinitions];
    renderableFields = [...renderableFields, ...visibleFields];

    // Remove duplicates based on field ID
    const uniqueFieldsMap = new Map<string, CustomField>();
    renderableFields.forEach((field) => {
      if (field && field.id) {
        uniqueFieldsMap.set(field.id, field);
      }
    });

    const result = Array.from(uniqueFieldsMap.values());

    // Cache the result
    this.renderableFieldDefinitionsCache[actualTicketTypeId] = result;

    return result;
  }

  // Load conversation titles for existing conversation links
  async loadConversationTitles() {
    // Only load for CREATE_CONVERSATION action type which has conversation links
    if (this.step?.data?.action?.type !== AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION) {
      return;
    }

    const actionData = this.step.data.action.data;
    if (!actionData?.conversationLinks?.length) {
      return;
    }

    const conversationLinks = actionData.conversationLinks;

    // Initialize lookups object and conversationId lookup if they don't exist
    if (!this.lookups) {
      this.lookups = {};
    }
    if (!this.lookups.conversationId) {
      this.lookups.conversationId = {};
    }

    // Get conversation IDs that don't have titles loaded yet
    const conversationIdsToLoad = conversationLinks
      .map((link) => {
        const linkedConversationId = link.linkedConversationId;
        // Only extract actual string IDs, skip merge fields (valueFrom)
        if (typeof linkedConversationId === 'string') {
          return linkedConversationId;
        } else if (typeof linkedConversationId === 'object' && linkedConversationId?.value) {
          return linkedConversationId.value;
        }
        // Skip merge fields and other non-string values
        return null;
      })
      .filter((id) => id && !(this.lookups.conversationId && this.lookups.conversationId[id])); // Added null check

    if (!conversationIdsToLoad.length) {
      return;
    }

    try {
      // Load conversations in batch
      const response: any = await this.apiService.postPromise('/conversations/list', {
        where: [
          {
            field: 'id',
            operator: 'in',
            value: conversationIdsToLoad,
          },
        ],
        limit: conversationIdsToLoad.length,
      });

      // Update lookups with conversation titles
      if (response?.data) {
        response.data.forEach((conversation: any) => {
          this.lookups.conversationId[conversation.id] =
            conversation.title || conversation.friendlyId || conversation.id;
        });
      }

      // Trigger change detection to update the UI
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      console.error('Failed to load conversation titles:', error);
      // Fallback: at least populate the lookup with the IDs so they don't keep trying to load
      // Ensure lookups structure exists first
      if (!this.lookups) {
        this.lookups = {};
      }
      if (!this.lookups.conversationId) {
        this.lookups.conversationId = {};
      }
      conversationIdsToLoad.forEach((id) => {
        if (!this.lookups.conversationId[id]) {
          this.lookups.conversationId[id] = id;
        }
      });
    }
  }

  getConversationLinkIcon(type: string): string {
    return CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[type] || 'link-outline';
  }

  getConversationLinkLabel(link: any): string {
    const typeLabel = CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[link.type] || link.type;

    if (!link.linkedConversationId) {
      return `${typeLabel}: Select conversation`;
    }

    const conversationLabel = this.lookups?.conversationId?.[link.linkedConversationId] || link.linkedConversationId;
    return `${typeLabel}: ${conversationLabel}`;
  }

  // Create Conversation Channel Selection Methods

  getConversationChannel(actionData: any): SlackChannel | undefined {
    if (!actionData.channelId) return undefined;

    // Extract the channel ID from the resolvable value
    const channelId =
      typeof actionData.channelId === 'object' && 'value' in actionData.channelId
        ? actionData.channelId.value
        : actionData.channelId;

    if (!channelId) return undefined;

    // Return channel with name from lookups if available, otherwise show loading or ID
    const channelName = this.lookups?.slackChannelId?.[channelId];
    return {
      id: channelId,
      name: channelName || `Loading channel...`,
    } as SlackChannel;
  }

  onConversationChannelChanged(actionData: any, channel: SlackChannel | undefined) {
    if (channel) {
      actionData.channelId = { value: channel.id };

      // Update lookups for display
      if (!this.lookups.slackChannelId) {
        this.lookups.slackChannelId = {};
      }
      this.lookups.slackChannelId[channel.id] = channel.name;
    } else {
      actionData.channelId = null;
    }
  }

  // Conversation Links Methods
  shouldShowConversationLinks(): boolean {
    // Only show for button click and event triggers
    return this.triggerType === 'button-click' || this.triggerType === 'event';
  }

  selectConversationLinkType(actionData: any) {
    const linkTypeOptions = [
      {
        id: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.RELATED_TO,
        title: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.RELATED_TO],
        icon: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.RELATED_TO],
      },
      {
        id: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.BLOCKS,
        title: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.BLOCKS],
        icon: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.BLOCKS],
      },
      {
        id: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.BLOCKED_BY,
        title: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.BLOCKED_BY],
        icon: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.BLOCKED_BY],
      },
      {
        id: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.TRACKS,
        title: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.TRACKS],
        icon: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.TRACKS],
      },
      {
        id: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.TRACKED_BY,
        title: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.TRACKED_BY],
        icon: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.TRACKED_BY],
      },
    ];

    this.searchablePopoverService.showPopover({
      event,
      showSearch: false,
      selectedItemId: actionData.conversationLinks?.[0]?.type,
      callback: (id: string, name: string) => {
        if (!actionData.conversationLinks) {
          actionData.conversationLinks = [];
        }

        // Only allow a single link - replace existing or create new
        actionData.conversationLinks = [
          {
            type: id,
            linkedConversationId: { valueFrom: 'conversation.id' }, // Link to triggering conversation
          },
        ];
      },
      loadData: async () => linkTypeOptions,
    });
  }

  getSelectedConversationLinkTypeLabel(actionData: any): string {
    const linkType = actionData.conversationLinks?.[0]?.type;
    if (!linkType) return '';

    return CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[linkType] || linkType;
  }
}
